// The navigation model. One place, consumed by the Y-axis, the X-axis, the
// route table and every area session that follows.
//
// WHY THIS FILE EXISTS SEPARATELY FROM THE SHELL COMPONENT
// =======================================================
// Five sessions build the five areas. If the area list lived inside the shell
// component, each of them would have to open the shell to add an X-axis entry,
// and five sessions editing one component is the exact collision this structure
// exists to prevent. The shell renders whatever this file declares; an area
// session edits its own entry here and its own stub under `routes/`.
//
// THE Y-AXIS IS LOCKED AT FIVE (v1.1 §2, §10)
// ===========================================
// Brain · Workstation · Operations · Executive Assistant · Settings. Nothing
// else. No standalone Analytics, Research or Creative area — Q3 and §10 forbid
// it, and `AREAS.length === 5` is asserted by the shell's own test rather than
// left to reviewer memory.
//
// FOUNDER GATING IS NAVIGATION, NOT ACCESS CONTROL (R5)
// =====================================================
// `founderOnly` here decides what a non-founder SEES. It decides nothing about
// what a non-founder can REACH: every founder-only destination is refused by
// the server, and the client gate is a courtesy that keeps the navigation
// honest. `display: none` is not access control, and neither is this flag.
// Anything that would be unsafe if a non-founder typed the URL must be refused
// by `require_founder` on the backend — which it already is, 37 times over.

export const FOUNDER = "FOUNDER";

/** True when the authenticated operator is the Founder. Never a name. */
export function isFounder(user) {
  return user?.role === FOUNDER;
}

/**
 * The Y-axis, in ratified order.
 *
 * `path` is the route base under `/v2`. Every entry resolves to a real route —
 * Settings included. `shell.js` in the design package leaves Settings pointing
 * at a dead anchor; Step 2's Definition of Done greps for exactly that pattern,
 * so it is a real path here and the stub behind it is a real component.
 */
export const AREAS = [
  {
    id: "brain",
    label: "Brain",
    path: "/v2/brain",
    icon: "brain",
    founderOnly: false,
  },
  {
    id: "workstation",
    label: "Workstation",
    path: "/v2/workstation",
    icon: "work",
    founderOnly: false,
  },
  {
    id: "operations",
    label: "Operations",
    path: "/v2/operations",
    icon: "ops",
    founderOnly: false,
  },
  {
    id: "ea",
    label: "Executive Assistant",
    path: "/v2/ea",
    icon: "ea",
    founderOnly: false,
  },
  {
    id: "settings",
    label: "Settings",
    path: "/v2/settings",
    icon: "set",
    founderOnly: false,
  },
];

/**
 * The contextual X-axis per area, verbatim from `SHELL.md` except where a §7
 * correction says otherwise. Both exceptions are marked and both are recorded
 * in the night's log rather than made silently.
 *
 * `tab` is the URL segment; the area's index route redirects to the first tab
 * its viewer is allowed to see.
 */
export const XNAV = {
  // SHELL.md: "Brain (founder): Brain Home · Organization Health · Operator
  // Analytics · Pending Decisions · Organization Map. Non-founder: Brain Home
  // only (founder destinations are absent — access is gated in nav *and*
  // data/actions, §3.1)."
  brain: [
    { tab: "", label: "Brain Home", founderOnly: false },
    { tab: "health", label: "Organization Health", founderOnly: true },
    { tab: "analytics", label: "Operator Analytics", founderOnly: true },
    { tab: "decisions", label: "Pending Decisions", founderOnly: true },
    { tab: "map", label: "Organization Map", founderOnly: true },
    // V2PORT-WS3 (Founder-approved 2026-08-28): the capability registry —
    // every Skill and MCP connection, with the four dimensions kept apart.
    // Founder-only like the other organization surfaces; the backend routes are
    // `require_founder` AND flag-gated behind CIPHER_CAPABILITY_API_V1
    // regardless (R5 — this entry is navigation, not access control).
    { tab: "skills", label: "Skills & MCP", founderOnly: true },
    // REASONING-STAGE-A-OPERATOR-PATH (Founder-directed, post-SHELL.md): the
    // Head-of-Testing reasoning pilot's operator panel. Founder-only like the
    // other organization surfaces; the backend routes are `require_founder`
    // and flag-gated regardless (R5 — this entry is navigation, not access
    // control).
    { tab: "stage-a", label: "Stage A Pilot", founderOnly: true },
  ],

  workstation: [
    { tab: "", label: "Overview", founderOnly: false },
    { tab: "analytics", label: "Analytics", founderOnly: false },
    // Split 2026-09-09 (RESEARCH-DECISION-BRIEF-BUILD, Founder ruling).
    // WORKSTATION_RESEARCH.md §5 already excluded audience statistics from
    // Research: they are "a different data source (our own ads, not third-party
    // research)". Research is what the market says; Audience is what happened
    // when we spent money.
    { tab: "research", label: "Research", founderOnly: false },
    { tab: "audience", label: "Audience", founderOnly: false },
    { tab: "landing-pages", label: "Landing Pages", founderOnly: false },
    { tab: "creative", label: "Ads & Creative", founderOnly: false },
    { tab: "traffic", label: "Traffic Channels", founderOnly: false },
    { tab: "learnings", label: "Learnings", founderOnly: false },
    // ABH creative compliance review — advisory, never a launch gate.
    { tab: "compliance", label: "Compliance", founderOnly: true },
    // A3 — Opportunities is backed: it reads getAtRisk / getScaleCandidates /
    // getCampaignDiagnosis (see workstation/Opportunities.jsx), all real,
    // working endpoints. No gap here.
    { tab: "opportunities", label: "Opportunities", founderOnly: false },
  ],

  // C1 — `operations.html` marks Overview active; §7.1 is LOCKED and states the
  // default view is "Now", and §3.8 gives `/operations` the new home
  // "Operations → Now". The build instruction assigns that fix to STEP 7, so
  // the structure here is SHELL.md's verbatim and the correction is NOT applied
  // early. Step 7 renames/reorders the first entry. Logged for the morning.
  operations: [
    { tab: "", label: "Now", founderOnly: false },
    { tab: "workflows", label: "Workflows", founderOnly: false },
    { tab: "automation", label: "Automation", founderOnly: false },
    { tab: "recommendations", label: "Recommendations", founderOnly: false },
    { tab: "history", label: "History", founderOnly: false },
  ],

  // C2 — SHELL.md lists "Upcoming Meetings" as the fourth EA entry. C2 states
  // plainly that meeting intelligence DOES NOT EXIST, that the mockup presents
  // a target as working, and: "Do not carry any of these four into the React
  // build." Unlike C1 it defers to no later step, so the tab is absent here
  // rather than reserved — a reserved tab is still a promise on screen.
  // Logged for the morning as a deliberate deviation from SHELL.md.
  //
  // Conversation is the EA home itself, not an X-axis item (SHELL.md).
  ea: [
    { tab: "", label: "Daily Brief", founderOnly: false },
    { tab: "priorities", label: "Priorities", founderOnly: false },
    { tab: "follow-ups", label: "Follow-ups", founderOnly: false },
  ],

  // §9 says Settings inherits the design system and needs no separate design
  // exercise; `surfaces/settings.md` is the spec. These are the groupings §3.10
  // and §8/A1 name. A1's proposed reading puts diagnostics under Infrastructure.
  settings: [
    { tab: "", label: "My Settings", founderOnly: false },
    { tab: "offers", label: "Offers & Campaigns", founderOnly: false },
    { tab: "system", label: "System Configuration", founderOnly: true },
    { tab: "infrastructure", label: "Infrastructure", founderOnly: true },
  ],
};

/** The Y-axis entries this operator may see. Never a hardcoded identity. */
export function visibleAreas(user) {
  const founder = isFounder(user);
  return AREAS.filter((a) => !a.founderOnly || founder);
}

/** The X-axis entries this operator may see, for one area. */
export function visibleTabs(areaId, user) {
  const founder = isFounder(user);
  return (XNAV[areaId] || []).filter((t) => !t.founderOnly || founder);
}

/** The area whose `path` prefixes this location, or undefined. */
export function areaForPath(pathname) {
  return AREAS.find(
    (a) => pathname === a.path || pathname.startsWith(`${a.path}/`)
  );
}
